import type {
  CSSProperties,
  MouseEvent as ReactMouseEvent,
  PointerEvent as ReactPointerEvent,
  ReactNode,
  RefObject,
} from 'react';
import type { useCamera } from './useCamera';

/**
 * The dotted surface a plane is drawn on, and the camera over it.
 *
 * Shared by every plane — an arc's and a nook's — so pan, zoom and the gestures
 * a canvas is expected to have behave the same wherever there is one. What sits
 * ON the surface, and what a press on it means, stays with the page:
 * [onPointerDown] only hears a press the camera did not take for a pan.
 */
export function PlaneSurface({
  cam,
  surfaceRef,
  className,
  style,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  onDoubleClickEmpty,
  claimsX,
  children,
  overlay,
}: {
  cam: ReturnType<typeof useCamera>;
  surfaceRef: RefObject<HTMLDivElement | null>;
  className?: string;
  style?: CSSProperties;
  onPointerDown?: (event: ReactPointerEvent) => void;
  onPointerMove?: (event: ReactPointerEvent) => void;
  onPointerUp?: (event: ReactPointerEvent) => void;
  /** A plain double-click on empty canvas, where the page has a use for it; zoom is then Alt
   *  (in) and Shift (out). */
  onDoubleClickEmpty?: (event: ReactMouseEvent) => void;
  /** Whether the surface keeps a sideways swipe from the browser's back gesture: set, it says
   *  `data-claims-x` for the stylesheet to read. */
  claimsX?: boolean;
  /** Drawn in canvas space, under the camera. */
  children: ReactNode;
  /** Drawn in screen space, over the canvas. */
  overlay?: ReactNode;
}) {
  const up = (event: ReactPointerEvent) => {
    cam.endPan();
    onPointerUp?.(event);
  };

  return (
    <div
      ref={surfaceRef}
      className={`plane${className ? ` ${className}` : ''}`}
      style={style}
      data-claims-x={claimsX}
      onWheel={cam.onWheel}
      onPointerDown={(event) => {
        if (cam.wantsPan(event)) {
          cam.beginPan(event);
          return;
        }
        onPointerDown?.(event);
      }}
      // Double-tap zooms toward what is under the pointer, with shift to go
      // back out — the gesture a canvas is expected to have, and one that
      // needs no trackpad fingers the browser has already claimed. A page that
      // writes on its canvas takes the plain double-click for that instead.
      onDoubleClick={(e) => {
        if ((e.target as HTMLElement).closest('[data-card]')) return;
        if (onDoubleClickEmpty && !e.altKey && !e.shiftKey) {
          onDoubleClickEmpty(e);
          return;
        }
        const rect = e.currentTarget.getBoundingClientRect();
        cam.zoomAt(e.clientX - rect.left, e.clientY - rect.top, e.shiftKey ? 1 / 1.4 : 1.4);
      }}
      onContextMenu={(e) => e.preventDefault()}
      onPointerMove={(event) => {
        cam.onPointerMove(event);
        onPointerMove?.(event);
      }}
      onPointerUp={up}
      onPointerLeave={up}
    >
      <div
        className="plane-canvas"
        style={{
          transform: `translate(${cam.camera.x}px, ${cam.camera.y}px) scale(${cam.camera.zoom})`,
        }}
      >
        {children}
      </div>
      {overlay}
    </div>
  );
}
